import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";
import { toast } from "react-toastify";
import _ from "lodash";
import {
  Button,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  IconButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { getStatusAction } from "../../store/reducers/app";
import {
  getAllPicksAction,
  updateGroupPicksAction,
  updateThirdPlacePicksAction,
} from "../../store/reducers/picks";
import { openModal } from "../../store/reducers/modalConductor";
import { useContestId } from "../../hooks/useContestId";
import Spinner from "../Layout/Spinner";
import GroupPicks from "./GroupPicks";
import KnockoutPicks from "./KnockoutPicks";

// Build the rankings array (ordered list of pick_team_number) the API expects
// from a group's currently-visible team order.
const buildGroupRankings = (group) =>
  _.orderBy(group?.teams, ["groupPosition"], ["asc"]).map((t) => t?.teamNumber);

// Never persist a placeholder/empty ordering (-1 slots, missing numbers).
const isValidRankings = (rankings) =>
  !_.isEmpty(rankings) && !rankings.some((n) => !n || n === -1);

const Picks = (props) => {
  const { tab, focus } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  const numberOfTeams = 48;

  const userId = useSelector((state) => state.app?.userId);
  const picksProgress = useSelector((state) => state.app?.picksProgress);
  const statusLoading = useSelector((state) => state.app?.loading);
  const contestType = useSelector((state) => state.app?.contestType);
  const isKnockout = contestType === "Knockout";

  const groupPicks = useSelector((state) => state.picks?.groupPicks);
  const thirdPlacePicks = useSelector((state) => state.picks?.thirdPlacePicks);
  const picksLoading = useSelector((state) => state.picks?.loading);
  const koPicksSimple = useSelector((state) => state.picks?.koPicksSimple);

  const [searchParams, setSearchParams] = useSearchParams();
  const contestId = useContestId();

  const [showSpinner, setShowSpinner] = useState(true);
  const [currTab, setCurrTab] = useState(tab || "group");
  const [teamsInfo, setTeamsInfo] = useState([]);
  const [groups, setGroups] = useState([]);
  const [navGroups, setNavGroups] = useState([]);
  const [thirdPlaceTeams, setThirdPlaceTeams] = useState([]);
  const [koSubmittable, setKoSubmittable] = useState(false);
  const [groupProgress, setGroupProgress] = useState(0);
  const [saving, setSaving] = useState(false);

  // Sticky header offsets: the stepper + tab toggle pin beneath the frozen Nav,
  // and each tab's title block pins beneath that. Measure the Nav and this header
  // live (ResizeObserver) so the offsets stay exact across breakpoints and as the
  // games strip loads. headerStickyTop is handed to the tab panels for their own
  // sticky sub-headers.
  const headerRef = useRef(null);
  const [navOffset, setNavOffset] = useState(0);
  const [picksHeaderHeight, setPicksHeaderHeight] = useState(0);
  useEffect(() => {
    const nav = document.getElementById("app-nav");
    const header = headerRef.current;
    const update = () => {
      setNavOffset(nav ? nav.offsetHeight : 0);
      setPicksHeaderHeight(header ? header.offsetHeight : 0);
    };
    update();
    const ro = new ResizeObserver(update);
    if (nav) ro.observe(nav);
    if (header) ro.observe(header);
    return () => ro.disconnect();
  }, []);
  const headerStickyTop = navOffset + picksHeaderHeight;

  // A Knockout-type contest has no group stage — force the knockout tab so the
  // group picks flow is never shown, whatever the entry routing passes in.
  useEffect(() => {
    if (isKnockout) setCurrTab("knockout");
  }, [isKnockout]);

  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);

  const open = Boolean(menuAnchorEl);
  const handleMoreClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setMenuAnchorEl(null);
  };

  const findDisplayTeam = useCallback(
    (picks, useTeamNumber) => {
      if (!_.isEmpty(picks)) {
        const teamsForPicks = picks
          .map((pick) =>
            teamsInfo?.find((team) =>
              useTeamNumber
                ? // eslint-disable-next-line eqeqeq
                  team?.teamNumber == pick //some calls return team number as string
                : pick?.pick_team_number
                  ? team?.teamNumber === pick?.pick_team_number
                  : team?.teamNumber === pick?.team_number,
            ),
          )
          .sort((team) => team?.teamNumber);

        return teamsForPicks;
      }
    },
    [teamsInfo],
  );

  const orderPickedTeams = useCallback((groupPicks, completedGroups) => {
    let allTeams = [];

    const toDisplayFormat = _.mapValues(
      groupPicks,
      (teamsInGroup, groupName) => {
        const groupTeams = teamsInGroup.map((team) => {
          return {
            id: `group-${_.lowerCase(groupName)}-${_.replace(_.lowerCase(team.country_name), " ", "-")}`,
            name: team.country_name,
            teamNumber: team.pick_team_number,
            imageUrl: team.pick_flag,
            worldRank: team.pick_world_rank,
            group: groupName,
            groupPosition: team.group_standing_number,
            flaggedForEdit: false,
          };
        });

        const orderedTeams = _.orderBy(groupTeams, ["groupPosition"], ["asc"]);
        orderedTeams.forEach((team) => {
          allTeams.push(team);
        });

        const formattedGroup = {
          group: groupName,
          teams: orderedTeams,
          groupId: `group-picks-${_.lowerCase(groupName)}`,
          cardActive: completedGroups[groupName],
          rankingStartFrom: orderedTeams[0].teamNumber,
        };

        return formattedGroup;
      },
    );
    const groupTeams = _.values(toDisplayFormat);

    const formattedNavGroups = groupTeams.map((group) => ({
      ...group,
      complete: group?.cardActive,
    }));

    return {
      formattedGroups: groupTeams,
      formattedNavGroups,
      allTeams: _.uniqBy(allTeams, (team) => team?.teamNumber),
    };
  }, []);

  //update local state of progress trackers and active/focused card state when changes are made to the groups
  const setCardActive = useCallback(
    (isActive, groupName) => {
      const newGroups = groups?.map((group) => ({
        ...group,
        cardActive: group?.group === groupName ? isActive : group?.cardActive,
      }));
      const newNavGroups = navGroups.map((navGroup) => ({
        ...navGroup,
        complete: navGroup?.group === groupName ? isActive : navGroup?.complete,
      }));

      if (!_.isEmpty(newGroups)) {
        const updatedGroupProgress = newGroups.filter(
          (grp) => grp?.cardActive,
        ).length;
        setGroupProgress(updatedGroupProgress);

        setGroups(newGroups);
        setNavGroups(newNavGroups);
        if (!koSubmittable && picksProgress?.allPicksComplete) {
          setKoSubmittable(true);
        }
      }
    },
    [groups, navGroups, picksProgress?.allPicksComplete, koSubmittable],
  );

  //PAGE UPDATE FUNCTIONS
  useEffect(() => {
    //get initial page props for calculating display
    dispatch(getAllPicksAction({ userId, contestId }));
    dispatch(getStatusAction({ userId, contestId }));
  }, [dispatch, contestId, userId]);

  //Update local props when redux state updates (World Cup contest: teamsInfo +
  //groups are derived from the group picks). Skipped for a Knockout contest,
  //which has no group stage — its teamsInfo comes from the effect below.
  useEffect(() => {
    if (!isKnockout && !!groupPicks && !!picksProgress?.completedGroups) {
      const { formattedGroups, formattedNavGroups, allTeams } =
        orderPickedTeams(groupPicks, picksProgress.completedGroups);

      setGroups(formattedGroups);
      setNavGroups(formattedNavGroups);
      setTeamsInfo(allTeams);

      if (!_.isEmpty(formattedGroups)) {
        const updatedGroupProgress = formattedGroups.filter(
          (grp) => grp?.cardActive,
        )?.length;
        setGroupProgress(updatedGroupProgress);
      }

      setShowSpinner(false);
    }
  }, [
    isKnockout,
    groupPicks,
    orderPickedTeams,
    setGroupProgress,
    picksProgress?.completedGroups,
  ]);

  // Knockout contest: there are no group picks to build teamsInfo from, so
  // findDisplayTeam (which the KO chips resolve against) would map every team to
  // undefined. Source teamsInfo from the R32 field instead — koPicksSimple.round32
  // already carries each team's country_name / flag / world_rank, and the 32 R32
  // teams are every team that can appear in any knockout round.
  useEffect(() => {
    if (!isKnockout) return;
    const r32 = koPicksSimple?.round32;
    if (!_.isEmpty(r32)) {
      setTeamsInfo(
        r32.map((t) => ({
          id: `ko-team-${t.team_number}`,
          name: t.country_name,
          teamNumber: t.team_number,
          imageUrl: t.team_flag,
          worldRank: t.team_world_rank,
          flaggedForEdit: false,
        })),
      );
    }
    setShowSpinner(false);
  }, [isKnockout, koPicksSimple]);

  useEffect(() => {
    if (!_.isEmpty(thirdPlacePicks) && teamsInfo?.length === numberOfTeams) {
      const updatedThirdTeams = findDisplayTeam(thirdPlacePicks);

      setThirdPlaceTeams(updatedThirdTeams);
    }
  }, [thirdPlacePicks, findDisplayTeam, teamsInfo]);

  //UPDATE FUNCTIONS FOR CHILDREN
  const updateGroupPicks = useCallback(
    async (newPosition, initialPosition, currTeam, currGroup) => {
      const contestParam = parseInt(searchParams.get("contest_id"));
      const groupTeamsPicks = currGroup?.teams
        .map((team) => ({
          group_standing_number: team.groupPosition,
          pick_team_number: team.teamNumber,
        }))
        .sort((t) => t.group_standing_number)
        .map((t) => t.pick_team_number);

      const newTeamsOrder = [...groupTeamsPicks];
      const [removedTeam] = newTeamsOrder.splice(initialPosition, 1);
      newTeamsOrder.splice(newPosition, 0, removedTeam);

      try {
        const _updatedPicks = await dispatch(
          updateGroupPicksAction({
            userId,
            contestId: contestParam || 44,
            groupName: currGroup.group,
            rankings: newTeamsOrder,
          }),
        );
        const updatedPicks = _updatedPicks?.payload;
        const newGroupPicks = updatedPicks?.group_picks;

        if (!!newGroupPicks) {
          const newGroupTeams = _.mapValues(newGroupPicks, (picksArr) =>
            findDisplayTeam(picksArr),
          );

          let newGroups = [];
          Object.keys(newGroupTeams).forEach((key) => {
            const matchingGroup = groups.find((grp) => grp?.group === key);

            newGroups.push({
              ...matchingGroup,
              teams: newGroupTeams[key],
              cardActive:
                updatedPicks?.status?.user_status?.completed_groups[key] ||
                matchingGroup?.cardActive,
            });
          });

          setGroups(newGroups);
        }

        //if all groups are complete, run logic to calculate if we need to add a warning flag to third place teams
        if (
          updatedPicks?.status?.user_status?.all_groups_picks_complete ||
          picksProgress?.allGroupsComplete
        ) {
          const newThirdPlaceTeams = findDisplayTeam(updatedPicks?.third_place);
          let newThirdTeam = newThirdPlaceTeams?.find(
            (team) => team?.group === currTeam.group,
          );

          const updatedThirdTeams = newThirdPlaceTeams.map((team) => ({
            ...team,
            flaggedForEdit: newThirdTeam.teamNumber !== currTeam.teamNumber,
          }));

          setThirdPlaceTeams(updatedThirdTeams);
        }

        await dispatch(
          getStatusAction({ userId, contestId: contestParam || 44 }),
        );
      } catch (err) {
        console.error("Error updating group picks: ", err);
      }
    },
    [
      groups,
      searchParams,
      dispatch,
      findDisplayTeam,
      userId,
      picksProgress?.allGroupsComplete,
    ],
  );

  const updateThirdPlacePicks = useCallback(
    async (newPosition, initialPosition, currTeam, teams) => {
      const contestParam = parseInt(searchParams.get("contest_id"));
      const mappedPicks = teams
        .map((team) => ({
          group_standing_number: team?.groupPosition,
          pick_team_number: team?.teamNumber,
        }))
        .sort((t) => t.group_standing_number)
        .map((t) => t.pick_team_number);

      const newTeamsOrder = [...mappedPicks];
      const [removedTeam] = newTeamsOrder.splice(initialPosition, 1);
      newTeamsOrder.splice(newPosition, 0, removedTeam);

      try {
        const _updatedPicks = await dispatch(
          updateThirdPlacePicksAction({
            userId: userId,
            contestId: contestParam || 44,
            rankings: newTeamsOrder, //array of teamNumbers
          }),
        );
        const updatedPicks = _updatedPicks?.payload;

        const updatedTeams = findDisplayTeam(updatedPicks?.third_place);
        const newThirdPlaceTeams = updatedTeams?.map((team) => ({
          ...team,
          flaggedForEdit:
            team?.flaggedForEdit && team?.teamNumber === currTeam?.teamNumber
              ? false
              : team?.flaggedForEdit,
        }));

        setThirdPlaceTeams(newThirdPlaceTeams);

        await dispatch(
          getStatusAction({ userId, contestId: contestParam || 44 }),
        );
      } catch (err) {
        console.error("Error updating Third Place Picks: ", err);
      }
    },
    [searchParams, findDisplayTeam, userId, dispatch],
  );

  // Persist the order currently on screen for EVERY group (and the third-place
  // race), not just the ones the user dragged. Group picks otherwise only save
  // on a drag, so a user who accepts a group's default order never registers it
  // — its slots stay -1 and it never counts toward the Round of 32. This commits
  // those defaults. Only un-saved groups (cardActive === false) are sent, so a
  // full-default user costs at most 12 + 1 writes; an already-picked user, none.
  const commitVisiblePicks = useCallback(async () => {
    const contestParam = parseInt(searchParams.get("contest_id"));
    const contestId = contestParam || 44;
    let ok = true;

    for (const g of groups || []) {
      if (g?.cardActive) continue; // already saved
      const rankings = buildGroupRankings(g);
      // safety: never persist a placeholder/empty ordering
      if (!isValidRankings(rankings)) continue;

      const res = await dispatch(
        updateGroupPicksAction({
          userId,
          contestId,
          groupName: g?.group,
          rankings,
        }),
      );
      if (res?.meta?.requestStatus !== "fulfilled") ok = false;
    }

    // commit the third-place race default if it hasn't been saved yet
    if (!picksProgress?.thirdPlaceComplete && !_.isEmpty(thirdPlaceTeams)) {
      const rankings = thirdPlaceTeams.map((t) => t?.teamNumber);
      if (isValidRankings(rankings)) {
        const res = await dispatch(
          updateThirdPlacePicksAction({ userId, contestId, rankings }),
        );
        if (res?.meta?.requestStatus !== "fulfilled") ok = false;
      }
    }

    await dispatch(getStatusAction({ userId, contestId }));
    return ok;
  }, [groups, thirdPlaceTeams, picksProgress, userId, searchParams, dispatch]);

  // Accept a single group's visible order without requiring a drag. Used by the
  // per-group "Accept" button and the double-click-to-accept shortcut on a card.
  // The indicator (cardActive -> nav "complete") is only flipped AFTER the write
  // lands, so a blue/checked group always reflects a real persisted ranking.
  const commitGroup = useCallback(
    async (group) => {
      if (!group || group?.cardActive) return true; // nothing to do
      const contestId = parseInt(searchParams.get("contest_id")) || 44;
      const rankings = buildGroupRankings(group);
      if (!isValidRankings(rankings)) return false;

      const res = await dispatch(
        updateGroupPicksAction({
          userId,
          contestId,
          groupName: group?.group,
          rankings,
        }),
      );
      const ok = res?.meta?.requestStatus === "fulfilled";
      if (ok) {
        // flip the indicator only on success, then reconcile with the server
        setCardActive(true, group?.group);
        await dispatch(getStatusAction({ userId, contestId }));
      }
      return ok;
    },
    [searchParams, userId, dispatch, setCardActive],
  );

  // Accept the default top-8 third-place order without a drag. Only meaningful
  // once every group is registered (that's when the race is populated).
  const commitThirdPlace = useCallback(async () => {
    if (picksProgress?.thirdPlaceComplete) return true;
    const contestId = parseInt(searchParams.get("contest_id")) || 44;
    const rankings = (thirdPlaceTeams || []).map((t) => t?.teamNumber);
    if (!isValidRankings(rankings)) return false;

    const res = await dispatch(
      updateThirdPlacePicksAction({ userId, contestId, rankings }),
    );
    const ok = res?.meta?.requestStatus === "fulfilled";
    if (ok) await dispatch(getStatusAction({ userId, contestId }));
    return ok;
  }, [
    picksProgress?.thirdPlaceComplete,
    thirdPlaceTeams,
    searchParams,
    userId,
    dispatch,
  ]);

  //REPEATED COMPONENTS
  const stageIconSx = {
    color: "inherit",
    height: "20px",
    width: "20px",
  };

  const ProgressStageComponent = ({ icon, text, isComplete, isActive }) => (
    <Grid
      container
      direction="row"
      wrap="nowrap"
      sx={{
        gap: "12px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Grid
        container
        sx={[
          {
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            border: `2px solid ${isComplete || isActive ? theme.branding.teal : theme.branding.gray[500]}`,
            backgroundColor: isComplete ? theme.branding.teal : "none",
            color: isComplete
              ? theme.branding.gray[0]
              : isActive
                ? theme.branding.teal
                : theme.branding.gray[500],
            borderRadius: "100%",
          },
          (theme) =>
            theme.applyStyles("dark", {
              backgroundColor: isComplete
                ? theme.branding.softTeal
                : theme.branding.darkBlue[600],
              border: `2px solid ${isComplete || isActive ? theme.branding.softTeal : theme.branding.gray[0]}`,
              color: isComplete
                ? theme.branding.darkBlue[600]
                : isActive
                  ? theme.branding.softTeal
                  : theme.branding.gray[0],
            }),
        ]}
      >
        {icon}
      </Grid>
      {!isMobile && (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {text}
        </Typography>
      )}
    </Grid>
  );

  const progressLine = (isComplete) => (
    <Grid
      sx={[
        {
          height: "2px",
          backgroundColor: isComplete
            ? theme.branding.teal
            : theme.branding.gray[500],
          width: "100%",
          maxWidth: isMobile ? "36px" : "52px",
        },
        (theme) =>
          theme.applyStyles("dark", {
            backgroundColor: isComplete
              ? theme.branding.softTeal
              : theme.branding.gray[0],
          }),
      ]}
    />
  );

  //HIGHER ORDER PICKS SUBMISSION CALCULATIONS
  const handleTabChange = useCallback(
    (event, newTab) => {
      setCurrTab(newTab);
    },
    [setCurrTab],
  );

  // Stage toggle config. Rendered inside each tab's title row (GroupPicks /
  // KnockoutPicks) rather than in the page header, so the header stays flat.
  const pickTabs = isKnockout
    ? [{ label: "Knockout", value: "knockout" }]
    : [
        { label: "Group Stage", value: "group" },
        {
          label: "Knockout",
          value: "knockout",
          disabled: !picksProgress?.thirdPlaceComplete,
        },
      ];

  // The bracket (group stage + all six KO rounds) is what gates submission. The
  // tiebreaker is deliberately NOT part of this gate — it's collected inside the
  // submit modal, so requiring it here would make the modal that captures it
  // impossible to open. all_picks_complete (which includes the tiebreaker) only
  // becomes true AFTER the modal submits. completedKORounds is an object of six
  // booleans, so check each round explicitly.
  const bracketComplete = !!(
    (isKnockout || picksProgress?.groupStageComplete) &&
    picksProgress?.completedKORounds?.round32 &&
    picksProgress?.completedKORounds?.round16 &&
    picksProgress?.completedKORounds?.quarterFinal &&
    picksProgress?.completedKORounds?.semiFinal &&
    picksProgress?.completedKORounds?.final &&
    picksProgress?.completedKORounds?.thirdPlace
  );

  // Once an entry is submitted the Submit button grays out. It re-activates only
  // when the user changes a pick: the API clears is_picks_submitted in
  // recalculate_status on any pick mutation, and the group/third-place/knockout
  // handlers all refresh status, so picksSubmitted flips back to false here.
  const picksSubmitted = !!picksProgress?.picksSubmitted;
  const canSubmitEntry = bracketComplete && !picksSubmitted;

  const handleSubmitPicks = useCallback(() => {
    if (canSubmitEntry) {
      dispatch(
        openModal({
          modalType: "SUBMIT_PICKS_MODAL",
          modalProps: {
            show: true,
            userId,
            contestId,
          },
        }),
      );
    }
  }, [dispatch, contestId, userId, canSubmitEntry]);

  return (
    <>
      <Spinner show={showSpinner} fullScreen={true} />
      <TabContext value={currTab}>
        <Grid
          container
          direction="column"
          sx={{ width: "100%", justifyContent: "center" }}
        >
          {/* header — stepper + tab toggle, sticky beneath the frozen Nav */}
          <Grid
            container
            ref={headerRef}
            direction="column"
            sx={[
              {
                position: "sticky",
                top: `${navOffset}px`,
                zIndex: 60,
                backgroundColor: theme.branding.gray[0],
                width: "100%",
                gap: "24px",
                justifyContent: "center",
                alignItems: "center",
                padding: "24px 0px 16px",
              },
              (theme) =>
                theme.applyStyles("dark", {
                  backgroundColor: theme.branding.darkBlue[800],
                }),
            ]}
          >
            {/* progress stage tracker */}
            <Grid
              container
              direction="row"
              wrap="nowrap"
              sx={{
                maxWidth: "620px",
                width: "100%",
                padding: isMobile ? "0px 32px" : "none",
                gap: "16px",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {/* EDIT: add programmatic complete stage calculation */}
              {!isKnockout && (
                <>
                  <ProgressStageComponent
                    icon={<CheckOutlinedIcon sx={stageIconSx} />}
                    text="Group Picks"
                    isActive={true} //always shows as teal
                    isComplete={picksProgress?.thirdPlaceComplete}
                  />
                  {progressLine(picksProgress?.thirdPlaceComplete)}
                </>
              )}
              <ProgressStageComponent
                icon={<LockOutlinedIcon sx={stageIconSx} />}
                text="Finalize Bracket"
                isComplete={bracketComplete}
                isActive={isKnockout ? true : picksProgress?.thirdPlaceComplete}
              />
              {progressLine(bracketComplete)}
              <ProgressStageComponent
                icon={<RocketLaunchOutlinedIcon sx={stageIconSx} />}
                text="Submit Entry"
                isComplete={picksProgress?.picksSubmitted}
                isActive={bracketComplete}
              />
            </Grid>
          </Grid>

          {/* picks content — group picks suppressed for a Knockout contest */}
          {!isKnockout && (
          <TabPanel value="group" sx={{ padding: 0 }}>
            <Grid container sx={{ position: "relative" }}>
              <GroupPicks
                focus={focus}
                groups={groups}
                navGroups={navGroups}
                thirdPlaceTeams={thirdPlaceTeams}
                updateGroupPicks={updateGroupPicks}
                updateThirdPlacePicks={updateThirdPlacePicks}
                commitGroup={commitGroup}
                commitThirdPlace={commitThirdPlace}
                setCardActive={setCardActive}
                groupProgress={groupProgress}
                stickyTopOffset={headerStickyTop}
                tabs={pickTabs}
                currTab={currTab}
                handleTabChange={handleTabChange}
              />

              {/* sticky footer */}
              <Grid
                container
                id="sticky-footer-container"
                direction="row"
                wrap="nowrap"
                sx={[
                  {
                    padding: isMobile ? "8px 16px" : "16px 24px",
                    // iOS reserves a bottom strip (home indicator + browser
                    // toolbar). Without safe-area padding a fixed bottom bar
                    // renders beneath it, so the button is invisible on iPhone.
                    paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${
                      isMobile ? "8px" : "16px"
                    })`,
                    borderTop: `1px solid ${theme.branding.gray[200]}`,
                    backgroundColor: theme.branding.gray[0],
                    position: "fixed",
                    bottom: 0,
                    width: "100%",
                    maxWidth: `${theme.breakpoints.values.lg + 48}px`,
                    zIndex: 50,
                    alignItems: "center",
                    justifyContent: "space-between",
                  },
                  (theme) =>
                    theme.applyStyles({
                      borderColor: theme.branding.darkBlue[600],
                      backgroundColor: theme.branding.darkBlue[800],
                    }),
                ]}
              >
                <Grid
                  container
                  direction="row"
                  wrap="nowrap"
                  sx={{
                    gap: "12px",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {/* dummy button to "save" -- we are already saving picks data every time a change is made  */}
                  {!isMobile && (
                    <Button
                      variant="outlined"
                      startIcon={
                        <SaveOutlinedIcon
                          sx={{
                            color: "inherit",
                            height: "16px",
                            width: "16px",
                          }}
                        />
                      }
                      disabled={saving}
                      onClick={async () => {
                        setSaving(true);
                        const ok = await commitVisiblePicks();
                        setSaving(false);
                        toast(
                          ok
                            ? "Group Picks Saved!"
                            : "Some picks didn't save — please try again",
                          {
                            type: ok ? "success" : "error",
                            position: isMobile
                              ? "bottom-center"
                              : "bottom-right",
                            autoClose: 2000,
                            hideProgressBar: true,
                            closeOnClick: false,
                          },
                        );
                      }}
                      sx={{
                        borderRadius: "10px",
                        padding: "10px 24px",
                        borderColor: theme.branding.gray[200],
                        color: theme.branding.gray[500],
                      }}
                    >
                      <Typography
                        variant="button"
                        sx={{ fontWeight: 500, color: "inherit" }}
                      >
                        Save Draft
                      </Typography>
                    </Button>
                  )}

                  {/* clear all button */}
                  {/* <IconButton
                  variant="outlined"
                  onClick={handleMoreClick}
                  sx={[
                    {
                      borderRadius: "10px",
                      border: `1px solid ${theme.branding.gray[200]}`,
                    },
                    (theme) => theme.applyStyles("dark", {}),
                  ]}
                >
                  <MoreVertOutlinedIcon
                    sx={{ height: "20px", width: "20px" }}
                  />
                </IconButton>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={open}
                  onClose={handleMoreClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                >
                  <MenuItem
                    onClick={(e) => {
                      //EDIT: add call to reset all group picks
                      handleMoreClose(e);
                    }}
                    sx={{
                      color: theme.branding.red[500],
                      gap: "4px",
                    }}
                  >
                    <ListItemIcon
                      sx={{ minWidth: "0 !important", color: "inherit" }}
                    >
                      <DeleteOutlinedIcon
                        sx={{ width: "22px", color: "inherit" }}
                      />
                    </ListItemIcon>
                    <ListItemText sx={{ color: "inherit" }}>
                      <Typography
                        variant="body"
                        sx={{ color: "inherit", fontWeight: 500 }}
                      >
                        Clear All
                      </Typography>
                    </ListItemText>
                  </MenuItem>
                </Menu> */}
                </Grid>
                <Button
                  disabled={saving || !picksProgress?.thirdPlaceComplete}
                  variant="contained"
                  endIcon={
                    <ArrowForwardOutlinedIcon
                      sx={{ height: "16px", width: "16px", color: "inherit" }}
                    />
                  }
                  onClick={async (e) => {
                    setSaving(true);
                    await commitVisiblePicks();
                    setSaving(false);
                    handleTabChange(e, "knockout");
                  }}
                  sx={{
                    padding: "10px 32px",
                    borderRadius: "10px",
                    color: picksProgress?.thirdPlaceComplete
                      ? theme.branding.gray[0]
                      : "auto",
                    background: picksProgress?.thirdPlaceComplete
                      ? `linear-gradient(90deg, ${theme.branding.cyan[300]} 0%, ${theme.branding.cyan[700]} 100%)`
                      : "auto",
                  }}
                >
                  <Typography
                    variant="button"
                    sx={{ fontWeight: 700, color: "inherit" }}
                  >
                    Continue to Knockout
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </TabPanel>
          )}
          <TabPanel value="knockout" sx={{ padding: 0 }}>
            <KnockoutPicks
              focus={focus}
              isKnockout={isKnockout}
              searchParams={searchParams}
              groupProgress={groupProgress}
              findDisplayTeam={findDisplayTeam}
              stickyTopOffset={headerStickyTop}
              tabs={pickTabs}
              currTab={currTab}
              handleTabChange={handleTabChange}
            />

            {/* sticky footer */}
            <Grid
              container
              id="sticky-footer-container"
              direction="row"
              wrap="nowrap"
              sx={[
                {
                  padding: isMobile ? "8px 16px" : "16px 24px",
                  // iOS reserves a bottom strip (home indicator + browser
                  // toolbar). Without safe-area padding a fixed bottom bar
                  // renders beneath it, so the button is invisible on iPhone.
                  paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${
                    isMobile ? "8px" : "16px"
                  })`,
                  borderTop: `1px solid ${theme.branding.gray[200]}`,
                  backgroundColor: theme.branding.gray[0],
                  position: "fixed",
                  bottom: 0,
                  width: "100%",
                  maxWidth: `${theme.breakpoints.values.lg + 48}px`,
                  zIndex: 50,
                  alignItems: "center",
                  justifyContent: "flex-end",
                },
                (theme) =>
                  theme.applyStyles({
                    borderColor: theme.branding.darkBlue[600],
                    backgroundColor: theme.branding.darkBlue[800],
                  }),
              ]}
            >
              <Button
                disabled={!canSubmitEntry}
                variant="contained"
                fullWidth={isMobile}
                startIcon={
                  picksSubmitted ? (
                    <CheckOutlinedIcon
                      sx={{ height: "16px", width: "16px", color: "inherit" }}
                    />
                  ) : (
                    <SendOutlinedIcon
                      sx={{ height: "16px", width: "16px", color: "inherit" }}
                    />
                  )
                }
                onClick={handleSubmitPicks}
                sx={{
                  padding: "10px 32px",
                  borderRadius: "10px",
                  color: canSubmitEntry ? theme.branding.gray[0] : "auto",
                  background: canSubmitEntry
                    ? `linear-gradient(75deg, ${theme.branding.cyan[500]} 0%, ${theme.branding.cyan[300]} 80%)`
                    : "auto",
                }}
              >
                <Typography
                  variant="button"
                  sx={{ fontWeight: 700, color: "inherit" }}
                >
                  {picksSubmitted ? "Submitted" : "Submit Picks"}
                </Typography>
              </Button>
            </Grid>
          </TabPanel>
        </Grid>
      </TabContext>
    </>
  );
};

export default Picks;
